import { Button, Flex, Text } from '@retracedgmbh/ui-components-lib';
import { useAuth } from '@state/auth';
import { useEffect, useSyncExternalStore } from 'react';
import { useOpenFrontendV2 } from '@/hooks/useOpenFrontendV2';
import useTranslator from '@/hooks/useTranslator';
import {
  isV2BannerDismissed,
  setV2BannerDismissed,
  subscribeV2BannerDismiss,
} from '@/lib/v2BannerState';

const BANNER_HEIGHT_PX = 44;
const DEFAULT_CONTENT_PADDING_TOP = 58;

const FRONTEND_V2_URL = import.meta.env.VITE_FRONTEND_V2_URL;
const BANNER_ENABLED =
  import.meta.env.VITE_IS_FRONTEND_V2_BANNER_ENABLED === 'true';

export default function FrontendV2Banner() {
  const { getTranslation } = useTranslator({ page: 'navbar' });
  const me = useAuth(state => state.me);
  const { open: handleOpen, opening } = useOpenFrontendV2('navbar_banner');

  const dismissed = useSyncExternalStore(
    subscribeV2BannerDismiss,
    isV2BannerDismissed,
    () => false,
  );

  // Every page, every authenticated user — regular users, demo accounts and
  // Retraced staff (impersonating or not) alike.
  //
  // `/management` used to be excluded: first because it had no v2 counterpart,
  // then while that area was still behind a flag. It has one everywhere now,
  // and those are the pages that most need the jump — crossing between the
  // dashboards is a deliberate click on this banner, so hiding it there left
  // staff with no way over.
  const visible = BANNER_ENABLED && !!FRONTEND_V2_URL && !!me?.id && !dismissed;

  // Compensate hardcoded offsets in tailwind.css (#content padding-top: 58px,
  // .sidebar margin-top: 58px) so they don't get hidden under the taller fixed
  // wrapper (banner + navbar).
  useEffect(() => {
    if (!visible) return;
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-frontend-v2-banner', 'true');
    styleEl.textContent = `
      #content { padding-top: ${DEFAULT_CONTENT_PADDING_TOP + BANNER_HEIGHT_PX}px !important; }
      .sidebar { margin-top: ${DEFAULT_CONTENT_PADDING_TOP + BANNER_HEIGHT_PX}px !important; height: calc(100% - ${54 + BANNER_HEIGHT_PX}px) !important; }
    `;
    document.head.appendChild(styleEl);
    return () => {
      styleEl.remove();
    };
  }, [visible]);

  if (!visible) return null;

  const handleDismiss = () => {
    setV2BannerDismissed(true);
  };

  return (
    <Flex
      alignItems='center'
      gap='sm'
      paddingX='md'
      backgroundColor='brand'
      className='tw:text-white tw:h-11 tw:w-full'
    >
      <Text
        as='span'
        color='white'
        textOverFlow='truncate'
        className='tw:flex-1 tw:text-base'
        size='base'
      >
        {getTranslation('navbar.frontendV2Banner.message')}
      </Text>
      <Button
        type='button'
        variant='outline'
        onClick={handleOpen}
        disabled={opening}
        loading={opening}
      >
        {getTranslation('navbar.frontendV2Banner.cta')}
      </Button>
      <Button
        type='button'
        variant='ghost'
        size='icon'
        icon='fa-light fa-times'
        aria-label={getTranslation('navbar.frontendV2Banner.dismiss')}
        onClick={handleDismiss}
        className='tw:text-white'
      />
    </Flex>
  );
}
